import { ConversationsState } from '../../../lib/conversations';
import { Me } from '../../../lib/me';
import { usePageLoadBi } from '../../../lib/use-page-load-bi';
import { MobileSidebar } from './mobile/MobileSidebar';
import { WideSidebar } from './WideSidebar';
import { RailSidebar } from './rail/RailSidebar';
import type { NavId } from './hooks/use-sidebar-nav';

/**
 * The sidebar's ONE decision: which UI the viewport gets.
 *
 * Wide is a light floating rail that collapses to an icon strip; narrow is the
 * dark mobile drawer from the Figma design (`7593:24422`). They diverged far
 * enough that parameterising one tree meant a `narrow ?` on nearly every line, so
 * they are two standalone components — and every piece of shared LOGIC (which
 * destinations exist, the workspace rows, recent chats, credits, the account) lives
 * in ./hooks, which both read.
 */
export function Sidebar({
  state,
  selectedId,
  navCurrent,
  me,
  narrow,
  isOpen,
  onRequestClose,
  elevated = false,
  collapsed = false,
  onExpand,
  onOpenSearch,
  newRail = false,
}: {
  state: ConversationsState;
  selectedId: string | null;
  navCurrent?: NavId;
  me: Me | null;
  narrow: boolean;
  isOpen: boolean;
  onRequestClose?: () => void;
  elevated?: boolean;
  collapsed?: boolean;
  onExpand?: () => void;
  onOpenSearch?: () => void;
  /** The new desktop rail (`symphonyNewSidebarWeb`); the narrow drawer is the same either way. */
  newRail?: boolean;
}) {
  // The sidebar mounts beside every page, so its generic load must name ITSELF: left to the route it
  // reported the page underneath (`packages`, `chats`…), a second load of that page on every visit.
  // `drawer` is the name Android's drawer already reports (`Drawer.kt` `loadFinish("drawer", "drawer")`).
  usePageLoadBi({ page_name: 'menu', origin: 'menu' }, { pageName: 'drawer' });

  if (narrow) {
    return (
      <MobileSidebar
        state={state}
        selectedId={selectedId}
        navCurrent={navCurrent}
        me={me}
        isOpen={isOpen}
        onRequestClose={onRequestClose}
        elevated={elevated}
        onOpenSearch={onOpenSearch}
      />
    );
  }

  const Wide = newRail ? RailSidebar : WideSidebar;
  return (
    <Wide
      state={state}
      selectedId={selectedId}
      navCurrent={navCurrent}
      me={me}
      collapsed={collapsed}
      onExpand={onExpand}
      onOpenSearch={onOpenSearch}
    />
  );
}
